/**
 * Camera-continuity helpers for the panorama-bubble overlay.
 *
 * When the user enters a bubble from the 3D scene, we want to:
 *   1. capture the current Giro3D camera (position, orbit target, yaw) so we
 *      can restore it on close;
 *   2. read out the current yaw (survey azimuth) and pass it through as the
 *      bubble's `initialYawRad` so the bubble opens facing the same
 *      direction the user was already looking.
 *
 * The Giro3D adapter renders the world in an EPSG:3857 Z-up frame:
 *   +X = East, +Y = North, +Z = Up
 * Survey azimuth is measured from North (+Y), clockwise toward East (+X), so
 *   yaw = atan2(forward.x, forward.y)
 * where `forward` is the camera's world-direction vector (no horizontal
 * projection needed — atan2 only uses the X/Y components and is robust to
 * small pitch).
 */

export interface SavedGiro3DCameraState {
  /** Camera world position `[x, y, z]` in the engine scene frame. */
  readonly position: readonly [number, number, number];
  /** OrbitControls target (look-at point); preserved so the camera returns
   *  to the same framing, not just position. */
  readonly target: readonly [number, number, number];
  /** Survey azimuth (rad) the camera was facing when captured. */
  readonly yawRad: number;
}

interface RawVector3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

interface RawCamera {
  readonly position?: RawVector3;
  readonly getWorldDirection?: (out: { x: number; y: number; z: number }) => unknown;
}

interface RawControls {
  readonly target?: RawVector3 & {
    readonly set?: (x: number, y: number, z: number) => void;
  };
  readonly update?: () => void;
}

interface RawGiro3DInstance {
  readonly view?: { readonly camera?: RawCamera; readonly controls?: RawControls };
  readonly controls?: RawControls;
  readonly notifyChange?: () => void;
}

function parseInstance(instance: unknown): RawGiro3DInstance | null {
  if (!instance || typeof instance !== 'object') return null;
  return instance as RawGiro3DInstance;
}

function readCamera(instance: RawGiro3DInstance): RawCamera | null {
  return instance.view?.camera ?? null;
}

function readControls(instance: RawGiro3DInstance): RawControls | null {
  return instance.view?.controls ?? instance.controls ?? null;
}

function readVec3(v: RawVector3 | undefined | null): [number, number, number] | null {
  if (!v) return null;
  const { x, y, z } = v;
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
  return [x, y, z];
}

/**
 * Read the current Giro3D camera yaw (survey azimuth, rad) from the
 * adapter's internal Three.js camera. Returns `null` when the instance or
 * camera is not yet available (e.g. before the adapter has booted).
 */
export function getGiro3DCameraYawRad(instance: unknown): number | null {
  const inst = parseInstance(instance);
  const cam = inst ? readCamera(inst) : null;
  if (!cam || typeof cam.getWorldDirection !== 'function') return null;
  const out = { x: 0, y: 0, z: 0 };
  try {
    cam.getWorldDirection(out);
  } catch {
    return null;
  }
  if (!Number.isFinite(out.x) || !Number.isFinite(out.y)) return null;
  // EPSG:3857 / Giro3D scene frame: +X East, +Y North. Survey azimuth is
  // atan2(East, North). Works regardless of the camera's pitch because the
  // horizontal components alone define the heading.
  return Math.atan2(out.x, out.y);
}

/**
 * Snapshot the Giro3D camera state so it can be restored later (e.g. when
 * the user closes the panorama overlay). Returns `null` if the camera or
 * orbit target cannot be read.
 */
export function captureGiro3DCameraState(
  instance: unknown,
): SavedGiro3DCameraState | null {
  const inst = parseInstance(instance);
  if (!inst) return null;
  const cam = readCamera(inst);
  const ctrl = readControls(inst);
  const position = readVec3(cam?.position);
  const target = readVec3(ctrl?.target);
  if (!position || !target) return null;
  const yawRad = getGiro3DCameraYawRad(instance);
  if (yawRad == null) return null;
  return { position, target, yawRad };
}

/**
 * Restore a previously captured Giro3D camera state. The yaw is preserved
 * implicitly by setting both `camera.position` and `controls.target` —
 * OrbitControls derives the look direction from those two vectors, so
 * restoring them re-establishes the same heading the user had before
 * entering the bubble.
 */
export function restoreGiro3DCameraState(
  instance: unknown,
  state: SavedGiro3DCameraState,
): boolean {
  const inst = parseInstance(instance);
  if (!inst) return false;
  const cam = readCamera(inst) as
    | (RawCamera & {
      position?: RawVector3 & {
        set?: (x: number, y: number, z: number) => void;
      };
    })
    | null;
  const ctrl = readControls(inst);
  const setPos = cam?.position?.set;
  const setTarget = ctrl?.target?.set;
  if (typeof setPos !== 'function' || typeof setTarget !== 'function') {
    return false;
  }
  setPos.call(cam!.position, state.position[0], state.position[1], state.position[2]);
  setTarget.call(ctrl!.target, state.target[0], state.target[1], state.target[2]);
  if (typeof ctrl?.update === 'function') {
    try {
      ctrl.update();
    } catch {
      /* best-effort */
    }
  }
  if (typeof inst.notifyChange === 'function') {
    try {
      inst.notifyChange();
    } catch {
      /* best-effort */
    }
  }
  return true;
}
